const BLOG_POSTS = [
  {
    slug: 'how-to-read-security-insights-dashboard-panel',
    title: 'How To Read The Security Insights Panel In Your Dashboard',
    excerpt:
      'A section-by-section walkthrough of what each Security Insights block means and how to act on it.',
    author: 'Risqly Product Team',
    publishedAt: '2026-03-21',
    readTimeMinutes: 6,
    heroImage: '/email-assets/blog-network-aware-risk.svg',
    heroImageAlt: 'Dashboard-style illustration with layered security signals and risk indicators.',
    tags: ['Dashboard Guide', 'Security Insights', 'How-To'],
    sections: [
      {
        heading: 'Why This Panel Exists',
        paragraphs: [
          'The Security Insights panel translates score mechanics into concrete actions. Instead of only showing a final score, it highlights where risk pressure comes from and which improvement path is likely to move your score fastest.',
          'Use it as an action queue: resolve the highest-impact structural issues first, then move to technical hardening and policy maturity improvements.'
        ]
      },
      {
        heading: 'Verification Check',
        paragraphs: [
          'This section helps confirm that your public domain is correctly linked to your Risqly company page. Verification strengthens trust in profile ownership and reduces ambiguity in identity mapping.',
          'You can schedule a crawl for your root page or a specific subpage path. If a scan is already queued, the panel shows that status so you avoid duplicate scheduling.'
        ]
      },
      {
        heading: 'Risk Loops',
        paragraphs: [
          'Risk Loops detects whether your organization appears in a cyclical dependency path. Cycles matter because risk can reinforce itself through connected entities and cap score recovery potential.',
          'If a loop is detected, the panel shows a simplified visual, a legend, and a short list of vendors directly participating in that cycle. Breaking loop participation is often a high-impact move.'
        ]
      },
      {
        heading: 'Risk Driving Vendors',
        paragraphs: [
          'This section ranks vendors by effective score penalty so you can prioritize outreach and remediation by impact. It is intentionally score-focused and not merely alphabetical or volume-based.',
          'For top-penalty vendors, the panel may indicate potential score gain if the relationship risk is reduced or removed. Use this to sequence vendor interventions and dependency reduction plans.'
        ]
      },
      {
        heading: 'External Integrity & Policy Compliance',
        paragraphs: [
          'This block compares broad improvement categories and tells you where score leverage is currently strongest: policy maturity or external integrity posture.',
          'When no category has immediate leverage, the panel explicitly says so, which helps you avoid spending effort where it will not materially improve your score right now.'
        ]
      },
      {
        heading: 'External Integrity Improvements',
        paragraphs: [
          'These are concrete technical findings from the latest outside-in scan, such as TLS issues, missing secure headers, open ports, SPF/DMARC gaps, DNSSEC status, or CVE-linked risk signals.',
          'Each finding is paired with a short recommendation. Schedule a new outside-in scan after fixes to confirm improvements and refresh the recommendation list.'
        ]
      },
      {
        heading: 'Last Scan Details',
        paragraphs: [
          'The expandable details view provides deeper evidence: scan target metadata, score breakdown, network observations, website checks, transport/TLS posture, and email/DNS/CVE indicators.',
          'Use this when engineering teams need specifics for remediation tickets or when you want to validate exactly what changed between scans.'
        ]
      },
      {
        heading: 'Suggested Operating Rhythm',
        paragraphs: [
          'Review Security Insights at least weekly, and immediately after significant vendor or infrastructure changes. Track what changed, why it changed, and whether score deltas match expected outcomes.',
          'A practical sequence is: clear loop and top vendor pressures first, fix external integrity findings second, and then raise policy maturity where it gives incremental score gains.'
        ]
      }
    ]
  },
  {
    slug: 'how-to-pass-risqly-domain-verification',
    title: 'How To Pass Risqly Domain Verification With A Public Link',
    excerpt:
      'A practical guide for adding your Risqly company URL to your site in a way the verification scan can detect.',
    author: 'Risqly Verification Team',
    publishedAt: '2026-06-04',
    readTimeMinutes: 5,
    heroImage: '/email-assets/blog-verification-link-guide.svg',
    heroImageAlt: 'Illustration of a verified chain link between a company website and risqly.net profile.',
    tags: ['Verification', 'Domain Trust', 'Implementation Guide'],
    sections: [
      {
        heading: 'AI Instructions',
        aiPrompt: `# Task
Help me make my company website pass Risqly domain verification.

# Context
Risqly verifies company domain ownership by scanning a public page on my website and checking whether it can detect a clickable HTML anchor tag that points to my exact Risqly company profile URL.

# My Risqly Profile URL
{{RISQLY_PROFILE_URL}}

# What Risqly Looks For
- A public page that can be fetched without login.
- A clickable anchor tag, for example: \`<a href="{{RISQLY_PROFILE_URL}}">View our Risqly profile</a>\`.
- The exact expected Risqly profile URL.
- Prefer an absolute URL using \`https://risqly.net/company/...\`.
- The anchor should have visible link text or visible image alt text.
- A plain HTML anchor in the fetched page source is required.

# Required Implementation
Add the Risqly profile link to the global website footer. This is the preferred implementation because the footer is usually public, stable, and present across the site.

Only suggest another public location if adding a footer link is genuinely not possible in my website stack. If a fallback is needed, choose one stable public page and explain why the footer could not be used.

# Common Failure Cases To Avoid
- Do not use the wrong company profile URL.
- Do not change the Risqly domain or company path.
- Avoid typos in the normalized company name.
- Avoid relying only on runtime API responses, environment-specific config, third-party scripts, or client-only injection that may not be visible to the scan.
- Make sure the scanned page returns HTML content and the link is actually shipped as a visible anchor in the fetched page source.

# Final Step
After the footer link is published, open the Security Insights panel in the Risqly dashboard. In the verification section, schedule a scan for the homepage or another page where the shared footer appears.

# Output I Want From You
Give me exact implementation steps for my website stack, including where to place the footer link, what HTML or component code to add, and a checklist to confirm the page should pass Risqly verification.`
      },
      {
        heading: 'What The Verification Scan Looks For',
        paragraphs: [
          'Risqly verifies ownership by fetching your public page and checking whether the HTML contains a clickable anchor tag (<a href="...">) pointing to your exact Risqly company profile URL.',
          'The required format is a visible anchor in the fetched page source, preferably with an absolute URL such as {{RISQLY_PROFILE_URL}}. A URL that only appears inside JavaScript bundles, runtime config, or hidden markup will not pass verification.'
        ]
      },
      {
        heading: 'Recommended Implementation: Footer Trust Link',
        paragraphs: [
          'Add a small verification link in your global footer so it appears across your whole site. This is the preferred option because it is public, stable, and easy to maintain long term.',
          'Use another public page only if adding a footer link is not possible. If you must use a fallback page, choose one stable page such as a security, trust, or About page and schedule the verification scan for that exact path.',
          'Example: <a href="{{RISQLY_PROFILE_URL}}">View our Risqly profile</a>'
        ]
      },
      {
        heading: 'Common Reasons Verification Fails',
        paragraphs: [
          'Use the exact expected URL. Small mismatches can fail verification, such as a different path, wrong domain, extra redirects, or a typo in the normalized company name.',
          'Ensure the page responds with HTML content and that your URL is shipped as a visible anchor in the fetched page source. If your app injects the link dynamically from runtime APIs, environment-specific config, third-party scripts, or client-only JavaScript, it will not pass verification.'
        ]
      },
      {
        heading: 'Final Step: Schedule The Verification Scan',
        paragraphs: [
          'After publishing the link on your website, open the Security Insights panel in your Risqly dashboard. In the verification section, schedule a scan of the page where the link appears so Risqly can crawl your site and confirm the link is present.',
          'If you placed the link on a subpage, enter that path before scheduling the scan. Once the scan finds the link, your company can be promoted to fully verified.'
        ]
      }
    ]
  },
  {
    slug: 'building-network-aware-vendor-risk',
    title: 'Building a Network-Aware Vendor Risk Program',
    excerpt:
      'Why traditional one-to-one assessments miss systemic vendor exposure and how graph-aware monitoring closes the gap.',
    author: 'Risqly Research Team',
    publishedAt: '2026-03-15',
    readTimeMinutes: 4,
    heroImage: '/email-assets/blog-network-aware-risk.svg',
    heroImageAlt: 'Abstract network diagram illustrating connected vendor risk pathways.',
    tags: ['Vendor Risk', 'Network Security', 'Third-Party Risk'],
    sections: [
      {
        heading: 'Risk Does Not Stay Isolated',
        paragraphs: [
          'Most third-party programs evaluate a vendor relationship as if it exists in isolation. In reality, vendors share cloud providers, identity systems, and subcontractors. A single control failure can propagate across multiple organizations quickly.',
          'This creates hidden concentration risk. Two vendors may each appear healthy individually, but both can rely on the same weak fourth-party dependency.'
        ]
      },
      {
        heading: 'Use Relationship Data as a First-Class Signal',
        paragraphs: [
          'A more resilient model combines internal control posture with relationship context. Instead of asking only whether a vendor is secure today, ask how widely a failure could spread if that posture deteriorates.',
          'Network context helps triage remediation efforts. Teams can prioritize vendors whose weakness would have the largest cascading impact, not only those with the lowest standalone score.'
        ]
      },
      {
        heading: 'Start Lean and Expand Over Time',
        paragraphs: [
          'You do not need a perfect map of every dependency on day one. Begin with known critical vendors and shared platforms, then iteratively enrich the graph as new relationships are discovered.',
          'The key is consistency. A lightweight publishing cadence with clear risk narratives helps leadership track trends and make better investment decisions over time.'
        ]
      }
    ]
  }
];

export function getAllBlogPosts() {
  return [...BLOG_POSTS].sort((a, b) => new Date(b.publishedAt) - new Date(a.publishedAt));
}

export function getBlogPostBySlug(slug) {
  return BLOG_POSTS.find((post) => post.slug === slug);
}
